import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type Mandant } from '../lib/api'

/** Nur was gefüllt ist. Eine Zeile mit einem Strich sagt nichts. */
function Feld({ name, wert, mono }: { name: string; wert: string | number | null; mono?: boolean }) {
  if (wert === null || wert === '' || wert === undefined) return null
  return (
    <div className="feld">
      <dt>{name}</dt>
      <dd className={mono ? 'zahl' : undefined}>{wert}</dd>
    </div>
  )
}

function datum(wert: string | null): string | null {
  if (!wert) return null
  const d = new Date(wert)
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('de-DE')
}

/**
 * Was der Mandant macht, in den Worten des Beraters: Geschäftsmodell, Saison,
 * Standorte, Besonderheiten. Die KI liest das bei jeder BWA Analyse mit; es
 * erklärt vieles, was sonst auffällig wäre (Bargeld, Projektgeschäft, Ferien).
 */
function Steckbrief({ mandant: m }: { mandant: Mandant }) {
  const kunde = useQueryClient()
  const [text, setText] = useState(m.notiz ?? '')
  const speichern = useMutation({
    mutationFn: () => api.aendern(`/api/mandanten/${m.id}`, { notiz: text.trim() || null }),
    onSuccess: () => void kunde.invalidateQueries({ queryKey: ['mandant', m.id] }),
  })
  const geaendert = text.trim() !== (m.notiz ?? '').trim()
  return (
    <div className="steckbrief">
      <label htmlFor={`steckbrief-${m.id}`} className="steckbrief-titel">Steckbrief für die Analyse</label>
      <p className="hinweis">
        Was der Mandant macht: Geschäftsmodell, Standorte, Saison, Besonderheiten. Die KI liest das bei jeder BWA
        Analyse mit. Höchstens 2.000 Zeichen.
      </p>
      <textarea
        id={`steckbrief-${m.id}`}
        rows={6}
        maxLength={2000}
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          speichern.reset()
        }}
        placeholder="Etwa: Caterer für Kitas und Schulen, zwei Küchen, Juli und Dezember Ferienmonate mit wenig Umsatz."
      />
      <div className="steckbrief-fuss">
        {speichern.isError && <span className="fehler">{speichern.error instanceof Error ? speichern.error.message : 'Speichern hat nicht geklappt.'}</span>}
        {speichern.isSuccess && !geaendert && <span className="nebensache">Gespeichert</span>}
        <button type="button" className="knopf" disabled={!geaendert || speichern.isPending} onClick={() => speichern.mutate()}>
          {speichern.isPending ? 'Wird gespeichert …' : 'Speichern'}
        </button>
      </div>
    </div>
  )
}

/**
 * Stammdaten sind Nachschlagewissen, keine tägliche Arbeit — deshalb
 * zugeklappt. Aufgeklappt wird über <details>: das bringt Tastaturbedienung
 * und Vorleseprogramme mit, ohne dass wir sie nachbauen. Der Browsermarker
 * ist abgeschaltet, der Winkel ist unser eigener.
 */
export function Stammdaten({ mandant: m, name }: { mandant: Mandant; name: string }) {
  const jahr = m.wirtschaftsjahr_beginn ? new Date(m.wirtschaftsjahr_beginn) : null
  const wirtschaftsjahr =
    jahr && !Number.isNaN(jahr.getTime())
      ? jahr.getMonth() === 0 && jahr.getDate() === 1
        ? `Kalenderjahr (${jahr.getFullYear()})`
        : `abweichend, ab ${jahr.toLocaleDateString('de-DE')}`
      : null

  return (
    <details className="stammdaten">
      {/* Der Name selbst ist der Aufklapper. Jede eigene Zeile dafuer — ob
          "Stammdaten" oder die Kurzfassung — kostet Platz und sagt nichts,
          was der Klick nicht auch sagt. */}
      <summary title="Stammdaten anzeigen">
        <h1>{name}</h1>
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" className="winkel">
          <path
            d="M6.5 4 L11.5 9 L6.5 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </summary>

      <dl>
        <Feld name="Mandantennummer" wert={m.mandantennummer} mono />
        <Feld name="Beraternummer" wert={m.beraternummer} mono />
        <Feld name="Firmenname" wert={m.firmenname !== m.name ? m.firmenname : null} />
        <Feld name="Rechtsform" wert={m.rechtsform} />
        <Feld name="USt-IdNr." wert={m.ust_id} mono />
        <Feld name="Mandant seit" wert={datum(m.mandant_seit)} mono />

        <Feld name="Kontenrahmen" wert={m.kontenrahmen} />
        <Feld name="Sachkonten" wert={m.kontenlaenge ? `${m.kontenlaenge}-stellig` : null} />
        <Feld name="Kostenstellen" wert={m.kostenlaenge ? `${m.kostenlaenge}-stellig` : null} />
        <Feld name="Wirtschaftsjahr" wert={wirtschaftsjahr} />
        <Feld name="Versteuerung" wert={m.versteuerung} />
        <Feld name="Umsatzsteuervoranmeldung" wert={m.ustva_zeitraum} />
        <Feld name="Währung" wert={m.waehrung} />
        <Feld
          name="Historie in DATEV"
          wert={m.jahre_verfuegbar ? `${m.jahre_verfuegbar} Wirtschaftsjahre` : null}
        />
      </dl>

      <Steckbrief mandant={m} />

      {m.stammdaten_stand && (
        <p className="hinweis">
          Aus DATEV übernommen am {new Date(m.stammdaten_stand).toLocaleString('de-DE')}
        </p>
      )}
    </details>
  )
}
